// Set Yönetimi — kırılım grubu akışı (spec §6.3 DR6/DR15; KpiSetsPage 600 satır sınırı). Hedef
// (toplam [+ mevcut grup]) seçilince BreakdownGroupModal bağlamı set ağacı + katalogdan kurulur:
// aynı üstteki katalog boyutları pasif, hesap grubu varsa yeni grup doğrulama için, müşteriye özel
// toplamın grubu da müşteriye özel; üye kipinde "Katalogdan seç" = grubun sette olmayan üyeleri.
// Kayıt store `addBreakdownGroup` (tek tx) üzerinden; başarıda sayfa yeniden yükler, yeni satırlar
// kısa süre vurgulanır, dönüşen değer bandı (store `breakdownConverted`) kapatılana kadar durur.

import { useEffect, useMemo, useState } from "react";
import type { KpiBreakdownGroupCreateResult, KpiMetric, KpiSetIndicator } from "@mahir/shared";
import { BreakdownGroupModal } from "../../components/kpi/BreakdownGroupModal";
import { KpiBreakdownConvertedBand } from "../../components/kpi/KpiBreakdownConvertedBand";
import { groupLockFor, type GroupAddHandler } from "../../components/kpi/set-tree-menu";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { rootCodeOf } from "../../lib/kpi-breakdown-dimension-ui";
import { type GroupFormContext } from "../../lib/kpi-breakdown-group-form";
import { calcGroupDecisionFor, type SetBreakdownView } from "../../lib/kpi-breakdown-set";
import { useKpiStore } from "../../stores/kpi-store";

export interface SetBreakdownTarget {
  /** toplamın set satırı */
  totalId: string;
  /** üye ekleme kipi: grubun set satırı */
  groupId: string | null;
}

export function useSetBreakdown(args: {
  setId: string | null;
  clientId: string | null;
  clientName: string;
  draft: readonly KpiSetIndicator[];
  view: SetBreakdownView<KpiSetIndicator>;
  catalog: readonly KpiMetric[] | null;
  /** `kpi.catalog.categories` — yoksa katalog kategorisi altına grup önerilmez (TODO-KPI-128) */
  canManageCategories: boolean;
  /** kayıt sonrası: sayfa yeniden yükler + durum mesajı */
  onSaved: (message: string) => Promise<void>;
}): {
  open: (target: SetBreakdownTarget) => void;
  /** set ağacı ⋯ "+ Kırılım grubu ekle" (kilitli satırda menüde yok) */
  addGroup: GroupAddHandler;
  /** IndicatorModal düğmesi: kilitliyse undefined (düğme yok); `before` pencereyi kapatır */
  addGroupFor: (id: string, before: () => void) => (() => void) | undefined;
  modal: JSX.Element | null;
  band: JSX.Element | null;
  flashIds: ReadonlySet<string>;
} {
  const { setId, draft, view, catalog } = args;
  const [target, setTarget] = useState<SetBreakdownTarget | null>(null);
  const [flashMetricIds, setFlashMetricIds] = useState<ReadonlySet<string>>(() => new Set());
  const converted = useKpiStore((s) => s.breakdownConverted);
  const addBreakdownGroup = useKpiStore((s) => s.addBreakdownGroup);
  const dismiss = useKpiStore((s) => s.dismissBreakdownConverted);

  useEffect(() => {
    if (flashMetricIds.size === 0) return;
    const t = setTimeout(() => setFlashMetricIds(new Set()), 4000);
    return () => clearTimeout(t);
  }, [flashMetricIds]);

  const locked = useMemo(
    () => groupLockFor(draft, catalog, args.canManageCategories),
    [draft, catalog, args.canManageCategories],
  );
  const addGroup: GroupAddHandler = Object.assign(
    (id: string) => setTarget({ totalId: id, groupId: null }),
    { locked },
  );
  const addGroupFor = (id: string, before: () => void): (() => void) | undefined =>
    locked(id)
      ? undefined
      : () => {
          before();
          setTarget({ totalId: id, groupId: null });
        };

  const flashIds = useMemo(
    () => new Set(draft.filter((i) => flashMetricIds.has(i.metricId)).map((i) => i.id)),
    [draft, flashMetricIds],
  );

  const built = useMemo(() => {
    if (!target) return null;
    const picked = draft.find((i) => i.id === target.totalId);
    if (!picked) return null;
    // İkinci seviye (K-B9): hedef bir üyeyse grup onun altına kurulur; `total` aile köküdür.
    const memberGroup = view.isMember(picked.id) ? view.groupOf(picked.id) : null;
    let total = picked;
    if (memberGroup) {
      const seen = new Set<string>();
      let cur: typeof picked | null = picked;
      while (cur && view.isMember(cur.id) && !seen.has(cur.id)) {
        seen.add(cur.id);
        cur = view.totalOf(cur.id);
      }
      if (!cur) return null;
      total = cur;
    }
    const group = target.groupId ? draft.find((i) => i.id === target.groupId) : undefined;
    const byId = new Map((catalog ?? []).map((m) => [m.id, m]));
    const catalogGroups = (catalog ?? []).filter(
      (m) => m.parentId === picked.metricId && m.nodeKind === "breakdown_group",
    );
    const inSet = new Set(draft.map((i) => i.metricId));
    const groupMetric = group ? byId.get(group.metricId) : undefined;
    const ctx: GroupFormContext = {
      total: {
        metricId: total.metricId,
        code: total.code,
        label: total.labelOverride ?? total.label,
        unit: total.unit,
      },
      ...(memberGroup
        ? {
            parentMember: {
              label: picked.labelOverride ?? picked.label,
              groupLabel: memberGroup.labelOverride ?? memberGroup.label,
              metricId: picked.metricId,
            },
          }
        : {}),
      group: group
        ? {
            metricId: group.metricId,
            label: group.label,
            breakdown: group.breakdown ?? null,
            clientId: groupMetric?.clientId ?? null,
          }
        : null,
      usedDimensions: new Set(
        catalogGroups.map((g) => g.breakdown?.dimension).filter((d): d is string => !!d),
      ),
      calcGroupLabel:
        group || memberGroup ? null : (calcGroupDecisionFor(total, view).group?.label ?? null),
      totalClientId: byId.get(picked.metricId)?.clientId ?? null,
      rootCode: rootCodeOf(total.metricId, catalog ?? []),
    };
    const catalogMembers = group
      ? (catalog ?? [])
          .filter((m) => m.parentId === group.metricId && !inSet.has(m.id))
          .map((m) => ({ metricId: m.id, label: m.label }))
      : [];
    const existingMembers = group
      ? view.membersOf(group.id).map((m) => m.labelOverride ?? m.label)
      : [];
    return { ctx, catalogMembers, existingMembers };
  }, [target, draft, view, catalog]);

  async function onSaved(r: KpiBreakdownGroupCreateResult, groupLabel: string): Promise<void> {
    const C = KPI_BREAKDOWN_COPY.modal;
    const isNew = target?.groupId == null;
    setTarget(null);
    setFlashMetricIds(new Set([...(r.groupCreated ? [r.groupId] : []), ...r.memberIds]));
    await args.onSaved(
      isNew
        ? C.success(groupLabel, r.memberIds.length)
        : C.successMembers(groupLabel, r.memberIds.length),
    );
  }

  const modal =
    built && setId ? (
      <BreakdownGroupModal
        key={`${target?.totalId ?? ""}:${target?.groupId ?? ""}`}
        ctx={built.ctx}
        setId={setId}
        clientId={args.clientId}
        clientName={args.clientName}
        catalogMembers={built.catalogMembers}
        existingMembers={built.existingMembers}
        submit={addBreakdownGroup}
        onSaved={(r, label) => void onSaved(r, label)}
        onClose={() => setTarget(null)}
      />
    ) : null;

  const band =
    converted?.setId === setId ? (
      <div style={{ marginBottom: 12 }}>
        <KpiBreakdownConvertedBand
          kind="converted"
          converted={converted.values}
          onClose={dismiss}
        />
      </div>
    ) : null;

  return { open: setTarget, addGroup, addGroupFor, modal, band, flashIds };
}
